import { useState, useRef, useEffect } from "react";
import { Building2, MapPin, ChevronDown, Check } from "lucide-react";
import { cn } from "../../lib/utils";
import { useAuthStore } from "../../stores/authStore";
import { useAppStore } from "../../stores/appStore";

/** Derives selection state shared across TopBar, SideNav, and Dashboard:
 *  - `needsSiteSelection` is true when an org is chosen, no site is chosen,
 *    and that org has more than one site (so the user must pick one).
 *  - 0-site orgs (super-admins with no site assignment) bypass the gate
 *    because there is nothing to pick. */
export function useSiteSelection() {
  const { organizations } = useAuthStore();
  const { selectedOrgId, selectedSiteId } = useAppStore();
  const orgs = organizations || [];
  const selectedOrg = orgs.find((o) => o.id === selectedOrgId);
  const sites = selectedOrg?.virtual_sites || selectedOrg?.sites || [];
  return {
    orgs,
    selectedOrg,
    sites,
    selectedOrgId,
    selectedSiteId,
    needsSiteSelection:
      !!selectedOrgId && !selectedSiteId && sites.length > 1,
  };
}

export function OrgSiteSelector() {
  const { organizations } = useAuthStore();
  const { selectedOrgId, selectedSiteId, setSelectedOrg, setSelectedSite } =
    useAppStore();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const orgs = organizations || [];
  const selectedOrg = orgs.find((o) => o.id === selectedOrgId);
  const sites = selectedOrg?.virtual_sites || selectedOrg?.sites || [];
  const selectedSite = sites.find((s) => s.id === selectedSiteId);
  const needsSiteSelection =
    !!selectedOrgId && !selectedSiteId && sites.length > 1;

  useEffect(() => {
    if (orgs.length > 0 && !selectedOrgId) {
      setSelectedOrg(orgs[0].id);
    }
  }, [orgs, selectedOrgId, setSelectedOrg]);

  // Auto-pick the only site under the chosen org so the user doesn't have
  // to open the dropdown. When the org has multiple sites we leave the
  // selection blank and the rest of the app gates on `needsSiteSelection`.
  useEffect(() => {
    if (selectedOrgId && !selectedSiteId && sites.length === 1) {
      setSelectedSite(sites[0].id);
    }
  }, [selectedOrgId, selectedSiteId, sites, setSelectedSite]);

  // Auto-open the dropdown once when the user lands needing to pick a site,
  // so they don't have to hunt for the trigger. We only do this on the
  // initial transition into the prompt state.
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (needsSiteSelection && !autoOpenedRef.current) {
      autoOpenedRef.current = true;
      setIsOpen(true);
    }
    if (!needsSiteSelection) {
      autoOpenedRef.current = false;
    }
  }, [needsSiteSelection]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center gap-2 rounded-lg border border-surface-700 bg-surface-800/50 px-3 py-2 text-sm transition-all hover:border-surface-600",
          isOpen && "border-brand-500/50 ring-2 ring-brand-500/20",
          needsSiteSelection &&
            !isOpen &&
            "border-amber-500/60 ring-2 ring-amber-500/30 animate-pulse"
        )}
      >
        <Building2 className="h-4 w-4 text-brand-400" />
        <div className="flex flex-col items-start">
          <span className="text-surface-200 font-medium text-xs leading-tight">
            {selectedOrg?.name || "Select Organization"}
          </span>
          {selectedSite ? (
            <span className="text-surface-400 text-[10px] leading-tight">
              {selectedSite.name}
            </span>
          ) : (
            needsSiteSelection && (
              <span className="text-amber-300 text-[10px] leading-tight font-medium">
                Select a site
              </span>
            )
          )}
        </div>
        <ChevronDown
          className={cn(
            "h-4 w-4 text-surface-400 transition-transform",
            isOpen && "rotate-180"
          )}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 z-50 min-w-[280px] rounded-xl border border-surface-700 bg-surface-800 shadow-2xl shadow-black/40 overflow-hidden">
          <div className="p-2 space-y-1 max-h-80 overflow-y-auto">
            {orgs.map((org) => (
              <div key={org.id}>
                <button
                  onClick={() => {
                    setSelectedOrg(org.id);
                    if ((org.virtual_sites || org.sites || []).length === 0) setIsOpen(false);
                  }}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-surface-700",
                    selectedOrgId === org.id && "bg-surface-700/50"
                  )}
                >
                  <Building2 className="h-4 w-4 text-brand-400 shrink-0" />
                  <span className="text-surface-200 font-medium flex-1">
                    {org.name}
                  </span>
                  {selectedOrgId === org.id && !selectedSiteId && (
                    <Check className="h-4 w-4 text-brand-400" />
                  )}
                </button>

                {selectedOrgId === org.id && (org.virtual_sites || org.sites || []).length > 0 && (
                  <div className="ml-4 pl-2 border-l border-surface-700 space-y-0.5 my-1">
                    <button
                      onClick={() => {
                        setSelectedSite(null);
                        setIsOpen(false);
                      }}
                      className={cn(
                        "flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-left text-xs transition-colors hover:bg-surface-700",
                        !selectedSiteId && "bg-surface-700/50"
                      )}
                    >
                      <span className="text-surface-400">All Sites</span>
                      {!selectedSiteId && (
                        <Check className="h-3 w-3 text-brand-400 ml-auto" />
                      )}
                    </button>
                    {(org.virtual_sites || org.sites || []).map((site) => (
                      <button
                        key={site.id}
                        onClick={() => {
                          setSelectedSite(site.id);
                          setIsOpen(false);
                        }}
                        className={cn(
                          "flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-left text-xs transition-colors hover:bg-surface-700",
                          selectedSiteId === site.id && "bg-surface-700/50"
                        )}
                      >
                        <MapPin className="h-3 w-3 text-surface-500 shrink-0" />
                        <span className="text-surface-300 flex-1">
                          {site.name}
                        </span>
                        {site.code && (
                          <span className="text-[10px] text-surface-500">
                            {site.code}
                          </span>
                        )}
                        {selectedSiteId === site.id && (
                          <Check className="h-3 w-3 text-brand-400" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
